import { useId, type ComponentProps, type ReactNode } from 'react';
import { cx } from './util.ts';
import './Panel.css';

type HeadingLevel = 1 | 2 | 3 | 4;

export interface PanelProps extends Omit<ComponentProps<'section'>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  /** Acciones en la cabecera (botones, filtros). */
  actions?: ReactNode;
  headingLevel?: HeadingLevel;
}

/** Sección con título: región con nombre accesible para lectores de pantalla. */
export function Panel({
  title,
  description,
  actions,
  headingLevel = 2,
  className,
  children,
  ...rest
}: PanelProps) {
  const id = useId();
  const Heading = `h${headingLevel}` as const;
  return (
    <section aria-labelledby={id} className={cx('af-panel', className)} {...rest}>
      <header className="af-panel__header">
        <div>
          <Heading id={id} className="af-panel__title">
            {title}
          </Heading>
          {description && <p className="af-panel__description">{description}</p>}
        </div>
        {actions && <div className="af-panel__actions">{actions}</div>}
      </header>
      <div className="af-panel__body">{children}</div>
    </section>
  );
}

export interface CardProps extends ComponentProps<'div'> {
  /** control: puesto de control humano (banda ámbar); sunken: zona hundida. */
  tone?: 'default' | 'control' | 'sunken';
  elevated?: boolean;
}

export function Card({ tone = 'default', elevated = false, className, ...rest }: CardProps) {
  return (
    <div
      className={cx('af-card', `af-card--${tone}`, elevated && 'af-card--elevated', className)}
      {...rest}
    />
  );
}
